import { Spacing } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { type ReactNode, forwardRef, useImperativeHandle, useState } from 'react';
import { useMergeRefs } from 'reactApp/hooks/useMergeRefs';
import { clamp } from 'reactApp/utils/mathHelpers';

import styles from './CustomMarkerSlider.css';
import { useIsPressed } from './hooks/useIsPressed';
import { useMarks } from './hooks/useMarks';
import { useMove } from './hooks/useMove';
import { CustomMarkerSliderThumb } from './ui/Thumb/CustomMarkerSliderThumb';
import { convertFromAbs, convertToAbs, normalizeByStep } from './utils/value';

export interface CustomMarkerSliderHandler {
    setValue(value): void;
}

interface CustomMarkerSliderProps {
    min?: number;
    max: number;
    getStep: (value: number) => number;
    marks?: number[];
    minValueLabel: string;
    maxValueLabel: string;
    thumbSatelliteId?: string;
    thumbSatellite?: ReactNode;
    onChangeValue?(value: number): void;
}

export const CustomMarkerSlider = forwardRef<CustomMarkerSliderHandler, CustomMarkerSliderProps>(
    ({ marks = [], onChangeValue, minValueLabel, maxValueLabel, thumbSatelliteId, thumbSatellite, min = 0, max, getStep }, ref) => {
        const [value, setValue] = useState(0);
        const { pressed, ref: thumbRef } = useIsPressed();
        const { ref: sliderRef } = useMove(
            ({ x }) => {
                const _value = clamp(x, min / max, 1);
                setValue(_value);
                const absValue = convertToAbs(_value, min, max);
                onChangeValue?.(normalizeByStep(absValue, getStep(absValue)));
            },
            thumbRef,
            thumbSatelliteId
        );
        const widths = useMarks(marks);

        useImperativeHandle(
            ref,
            () => ({
                setValue(absValue) {
                    setValue(convertFromAbs(absValue, min, max));
                },
            }),
            [min, max]
        );

        return (
            <div className={styles.root}>
                <div className={styles.track} ref={useMergeRefs(ref, sliderRef)}>
                    <div
                        className={classNames(classNames(styles.filled, { [styles.withAnimation]: !pressed }))}
                        style={{
                            width: `calc(${value * 100}% - var(--thumb-width) / 2 - var(--thumb-offset) / 2)`,
                        }}
                    />
                    <div className={styles.emptyContainer}>
                        {widths.map((width, i) => (
                            <div key={i} style={{ width: `${width}%` }} className={styles.empty} />
                        ))}
                    </div>
                    <CustomMarkerSliderThumb pressed={pressed} ref={thumbRef} value={value} thumbSatellite={thumbSatellite} />
                </div>
                <Spacing size={10} />
                <div className={styles.bottom}>
                    <span className={styles.label}>{minValueLabel}</span>
                    <span className={styles.label}>{maxValueLabel}</span>
                </div>
            </div>
        );
    }
);

CustomMarkerSlider.displayName = 'CustomMarkerSlider';
